import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API, useAuth } from './AuthContext';

const PermissionsContext = createContext(null);

// Full-access permissions for admin/super_admin
const ADMIN_FULL = {
  dashboard:  { view: true },
  companies:  { view: true, create: true, edit: true, delete: true },
  contacts:   { view: true, create: true, edit: true, delete: true },
  pipeline:   { view: true, create: true, edit: true, delete: true },
  calendar:   { view: true, create: true, edit: true, delete: true },
  activities: { view: true, create: true, edit: true, delete: true },
  orders:     { view: true, create: true, edit: true, delete: true },
  invoices:   { view: true, create: true, edit: true, delete: true },
  analytics:  { view: true },
  reports:    { view: true },
  forecast:   { view: true, create: true, edit: true, delete: true },
  inventory:  { view: true, create: true, edit: true, delete: true },
  production: { view: true, create: true, edit: true, delete: true },
};

export function PermissionsProvider({ children }) {
  const { token } = useAuth();
  const [permissions, setPermissions] = useState(ADMIN_FULL);
  const [scope, setScope] = useState('all');
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async (tok) => {
    try {
      const t = tok || localStorage.getItem('agri_token');
      if (!t) { setPermissions(ADMIN_FULL); setScope('all'); setLoaded(true); return; }
      const res = await axios.get(`${API}/admin/permissions/me`, {
        headers: { Authorization: `Bearer ${t}` },
      });
      setPermissions(res.data.permissions || ADMIN_FULL);
      setScope(res.data.scope || 'all');
    } catch {
      // keep defaults on error
    } finally {
      setLoaded(true);
    }
  }, []);

  // Reload whenever token changes (login / logout / same-tab user switch)
  useEffect(() => {
    if (token) {
      load(token);
    } else {
      setPermissions(ADMIN_FULL);
      setScope('all');
      setLoaded(true);
    }
  }, [token, load]);

  const can = useCallback((module, action = 'view') => {
    if (!permissions) return false;
    const mod = permissions[module];
    if (!mod) return false;
    return mod[action] === true;
  }, [permissions]);

  const canView   = (module) => can(module, 'view');
  const canCreate = (module) => can(module, 'create');
  const canEdit   = (module) => can(module, 'edit');
  const canDelete = (module) => can(module, 'delete');

  return (
    <PermissionsContext.Provider value={{ permissions, scope, loaded, canView, canCreate, canEdit, canDelete, reload: load }}>
      {children}
    </PermissionsContext.Provider>
  );
}

export function usePermissions() {
  const ctx = useContext(PermissionsContext);
  if (!ctx) throw new Error('usePermissions must be used within PermissionsProvider');
  return ctx;
}
